import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, Image as ImageIcon, Link2, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface AdminUser {
  id: number;
  name: string;
  email: string;
  createdAt: string;
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
}

interface AdminSocialImage {
  id: string;
  url: string;
  title?: string | null;
  status: string;
  active: boolean;
  style: string;
  styleLabel: string;
  pageType?: string | null;
  domain?: string | null;
  createdAt: string;
  updatedAt: string;
  imageUrl?: string | null;
}

interface AdminUserSocialImagesPageProps {
  user: AdminUser;
  socialImages: AdminSocialImage[];
}

const numberFormatter = new Intl.NumberFormat();
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatPlanName(plan?: string | null) {
  if (!plan) return "—";
  return plan.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function subscriptionBadgeVariant(status?: string | null) {
  if (!status) return { label: "Unknown", variant: "outline" as const };

  const normalized = status.replace(/_/g, " ");
  if (status === "active") return { label: "Active", variant: "success" as const };
  if (status === "past_due") return { label: "Past due", variant: "secondary" as const };
  if (status === "canceled" || status === "incomplete_expired" || status === "unpaid") {
    return { label: normalized, variant: "destructive" as const };
  }

  return { label: normalized, variant: "outline" as const };
}

function statusBadge(status: string) {
  const normalized = status.replace(/_/g, " ");

  if (status === "completed") {
    return { label: "Completed", variant: "success" as const };
  }
  if (status === "failed") {
    return { label: "Failed", variant: "destructive" as const };
  }
  if (["pending", "processing", "screenshot", "metadata", "analyzing", "generating", "finalizing"].includes(status)) {
    return { label: normalized, variant: "secondary" as const };
  }

  return { label: normalized, variant: "outline" as const };
}

function formatPageType(pageType?: string | null) {
  if (!pageType) return "—";
  return pageType
    .split("_")
    .map((segment) => (segment.length > 0 ? `${segment[0].toUpperCase()}${segment.slice(1)}` : segment))
    .join(" ");
}

export default function AdminUserSocialImagesPage({ user, socialImages }: AdminUserSocialImagesPageProps) {
  const subscriptionBadge = subscriptionBadgeVariant(user.subscriptionStatus);

  return (
    <>
      <Head title={`Admin · ${user.name} · Social images`} />

      <div className="mx-auto w-full space-y-8 px-6 py-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase dark:text-slate-400">
              Social images
            </p>
            <div className="flex flex-col gap-1">
              <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-50">{user.name}</h1>
              <p className="text-slate-600 dark:text-slate-400">{user.email}</p>
              <p className="text-xs text-slate-500 dark:text-slate-500">
                Signed up {dateTimeFormatter.format(new Date(user.createdAt))}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={subscriptionBadge.variant}>{subscriptionBadge.label}</Badge>
            <Badge variant="outline">Plan: {formatPlanName(user.subscriptionPlan)}</Badge>
            <Button variant="outline" asChild>
              <Link href="/admin/users" preserveScroll>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to users
              </Link>
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-lg font-semibold">Generated images</CardTitle>
              <CardDescription>Every social image we have generated for this account.</CardDescription>
            </div>
            <Badge variant="outline" className="text-slate-600 dark:text-slate-300">
              {numberFormatter.format(socialImages.length)} total
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-950/50">
                    <TableRow>
                      <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Image
                      </TableHead>
                      <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Details
                      </TableHead>
                      <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Status
                      </TableHead>
                      <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Page type
                      </TableHead>
                      <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                        Created
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {socialImages.map((image) => {
                      const status = statusBadge(image.status);

                      return (
                        <TableRow key={image.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                          <TableCell className="px-6 py-4 align-top">
                            <div className="flex items-center gap-3">
                              {image.imageUrl ? (
                                <img
                                  src={image.imageUrl}
                                  alt={image.title ?? ""}
                                  className="h-16 w-28 rounded-lg object-cover shadow-sm ring-1 ring-slate-200 dark:ring-slate-800"
                                />
                              ) : (
                                <div className="flex h-16 w-28 items-center justify-center rounded-lg bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
                                  <ImageIcon className="h-5 w-5" />
                                </div>
                              )}
                              <div className="space-y-1">
                                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                  #{image.id}
                                </div>
                                <Badge variant={image.active ? "success" : "outline"}>
                                  {image.active ? "Active" : "Inactive"}
                                </Badge>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="px-6 py-4 align-top">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-amber-500" />
                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                  {image.title || "Untitled image"}
                                </span>
                              </div>
                              <div className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
                                <Link2 className="mt-0.5 h-3.5 w-3.5" />
                                <span className="break-all">{image.url}</span>
                              </div>
                              {image.domain && <Badge variant="outline">Domain: {image.domain}</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="px-6 py-4 align-top">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant={status.variant}>{status.label}</Badge>
                              <Badge variant="secondary">Style: {image.styleLabel}</Badge>
                            </div>
                          </TableCell>
                          <TableCell className="px-6 py-4 align-top">
                            <div className="flex flex-col gap-1">
                              <Badge variant="outline">{formatPageType(image.pageType)}</Badge>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Style key: {image.style}</div>
                            </div>
                          </TableCell>
                          <TableCell className="px-6 py-4 align-top">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {dateTimeFormatter.format(new Date(image.createdAt))}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              Updated {dateTimeFormatter.format(new Date(image.updatedAt))}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {socialImages.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="px-6 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                        >
                          No social images found for this user yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
